import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, X, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const RATINGS = [1, 2, 3, 4, 5];

export default function FeedbackWidget({ context = "app", capsuleId }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setMessage("");
    setRating(null);
    setEmail("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please write something before sending.");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/feedback", {
        message: message.trim(),
        rating: rating,
        capsule_id: capsuleId || null,
        email: email.trim() || null,
        context,
      });
      toast.success("Thanks! Your feedback landed in my inbox.");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send feedback. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="feedback-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-30 inline-flex items-center gap-2 border border-zinc-950 bg-rose-600 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white shadow-lg hover:bg-zinc-950 transition-colors"
        aria-label="Send feedback"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        Feedback
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4"
            onClick={() => setOpen(false)}
            data-testid="feedback-modal"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md border border-zinc-950 bg-white p-6 md:p-8"
              role="dialog" aria-modal="true"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-600">
                  Beta feedback
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-zinc-500 hover:text-zinc-950"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <h3 className="mt-3 font-display text-3xl font-bold tracking-tight">Tell me what you think</h3>
              <p className="mt-2 text-sm text-zinc-500">
                Bug, idea, complaint — anything. Goes straight to the developer.
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-5">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Rating (optional)</label>
                  <div className="mt-2 flex gap-1.5">
                    {RATINGS.map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`feedback-rating-${n}`}
                        onClick={() => setRating((r) => (r === n ? null : n))}
                        className={`p-1.5 transition-colors ${rating && n <= rating ? "text-rose-600" : "text-zinc-300 hover:text-zinc-500"}`}
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      >
                        <Star className={`h-6 w-6 ${rating && n <= rating ? "fill-current" : ""}`} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Your feedback
                  </label>
                  <textarea
                    id="fb-message"
                    data-testid="feedback-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="What worked? What didn't? What would you love to see?"
                    className="mt-2 block w-full border border-zinc-300 bg-white p-3 text-sm outline-none focus:border-zinc-950"
                  />
                  <p className="mt-1 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    {message.length}/2000
                  </p>
                </div>

                <div>
                  <label htmlFor="fb-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Email (optional — for follow-up)
                  </label>
                  <input
                    id="fb-email"
                    data-testid="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 block w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-zinc-950"
                  />
                </div>

                {capsuleId && (
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    Attaching capsule id · {capsuleId.slice(0, 8)}…
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="border border-zinc-300 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    data-testid="feedback-submit"
                    disabled={submitting || !message.trim()}
                    className="inline-flex items-center gap-2 bg-rose-600 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:bg-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    {submitting ? "Sending…" : "Send"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
